/**
 * Position Sizing Impact Mock
 *
 * Two equity curves from the same trade sequence — fixed-dollar
 * sizing vs percentage-risk sizing — showing how sizing alone
 * reshapes the equity curve with identical entries and exits.
 */

import { ResponsiveContainer, LineChart, Line, Tooltip, YAxis } from 'recharts';

// Same trade sequence (% returns): mix of wins and losses
const tradeReturns = [
  2.1, -1.0, 3.5, -0.8, -1.2, 4.1, -1.5, 1.8, -0.6, 2.9,
  -1.1, -0.9, 5.2, -1.3, 1.6, -0.7, 3.0, -1.4, 2.2, 1.1,
];

// Fixed-dollar: always risks $200 on a $10,000 account
// Returns are applied to the fixed amount, so equity grows linearly
function buildFixedCurve(): { x: number; v: number }[] {
  const fixedRisk = 200;
  let equity = 10000;
  const points = [{ x: 0, v: equity }];
  tradeReturns.forEach((r, i) => {
    equity += fixedRisk * (r / 1); // $200 * return multiple
    points.push({ x: i + 1, v: Math.round(equity) });
  });
  return points;
}

// Percentage-risk: always risks 2% of current equity
// Returns compound — wins grow the base, losses shrink it
function buildPctCurve(): { x: number; v: number }[] {
  let equity = 10000;
  const points = [{ x: 0, v: equity }];
  tradeReturns.forEach((r, i) => {
    equity += equity * 0.02 * (r / 1);
    points.push({ x: i + 1, v: Math.round(equity) });
  });
  return points;
}

const fixedData = buildFixedCurve();
const pctData = buildPctCurve();

const tipStyle = {
  background: 'rgba(17,24,39,0.95)',
  border: '1px solid rgba(75,85,99,0.5)',
  borderRadius: 6,
  fontSize: 11,
  fontFamily: 'ui-monospace, monospace',
};

export default function PositionSizingImpactMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Same Trades, Different Sizing
      </div>
      <div className="platform-mock-body">
        <div className="mock-compare">
          {/* Fixed sizing */}
          <div className="mock-compare-panel">
            <div className="mock-compare-title" style={{ color: '#6b7280' }}>
              Fixed Dollar ($200/trade)
            </div>
            <div style={{ width: '100%', height: 60 }}>
              <ResponsiveContainer>
                <LineChart data={fixedData} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                  <YAxis hide domain={['dataMin - 200', 'dataMax + 200']} />
                  <Tooltip
                    contentStyle={tipStyle}
                    labelStyle={{ display: 'none' }}
                    formatter={(v: number) => [`$${v.toLocaleString()}`, 'Equity']}
                    cursor={{ stroke: 'rgba(255,255,255,0.15)' }}
                  />
                  <Line type="monotone" dataKey="v" stroke="#6b7280" strokeWidth={1.5} dot={false} activeDot={{ r: 3, fill: '#6b7280' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              fontSize: '0.5625rem', color: '#6b7280', marginTop: '0.25rem',
            }}>
              <span>Start: $10,000</span>
              <span>End: ${fixedData[fixedData.length - 1].v.toLocaleString()}</span>
            </div>
          </div>

          {/* Percentage sizing */}
          <div className="mock-compare-panel">
            <div className="mock-compare-title" style={{ color: '#22c55e' }}>
              Percentage Risk (2% of equity)
            </div>
            <div style={{ width: '100%', height: 60 }}>
              <ResponsiveContainer>
                <LineChart data={pctData} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                  <YAxis hide domain={['dataMin - 200', 'dataMax + 200']} />
                  <Tooltip
                    contentStyle={tipStyle}
                    labelStyle={{ display: 'none' }}
                    formatter={(v: number) => [`$${v.toLocaleString()}`, 'Equity']}
                    cursor={{ stroke: 'rgba(255,255,255,0.15)' }}
                  />
                  <Line type="monotone" dataKey="v" stroke="#22c55e" strokeWidth={1.5} dot={false} activeDot={{ r: 3, fill: '#22c55e' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              fontSize: '0.5625rem', color: '#6b7280', marginTop: '0.25rem',
            }}>
              <span>Start: $10,000</span>
              <span>End: ${pctData[pctData.length - 1].v.toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div style={{
          marginTop: '0.75rem', padding: '0.5rem 0.75rem',
          background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)',
          borderRadius: 6, fontSize: '0.6875rem', color: '#9ca3af',
        }}>
          <span style={{ color: '#22c55e', fontWeight: 600 }}>Key insight: </span>
          Percentage sizing compounds — it naturally scales up on wins and scales
          down on losses. The same trade sequence produces a meaningfully different
          equity curve.
        </div>
      </div>
      <div className="platform-mock-caption">
        Position sizing is the hidden multiplier. Same trades, same entries, same exits — different outcomes.
      </div>
    </div>
  );
}
